// Раздел «Места»: город и даты в шапке, открытый подраздел, применённый контекст (по нему грузятся подразделы),
// город по умолчанию, переходы из других разделов (окно билета, карточка отеля) и по ссылке
import type { ExploreContext } from '../../entities/poi';
import { currentQuery, formStore } from '../flight-search';
import { today } from '../../shared/lib/format';
import { store as storage } from '../../shared/lib/storage';
import { createStore, useStore } from '../../shared/lib/store';
import type { Picked } from '../../shared/ui/Autocomplete';
import { cityLabel, defaultCity, makeContext, parseLink, type ExploreTab } from './model';

const LAST_CITY = 'explore:lastCity';

export interface ExploreState {
  city: Picked | null;
  date: string;
  dateTo: string;
  tab: ExploreTab;
  /** «рядом с отелем» */
  near: ExploreContext['near'];
  /** применённый контекст: подразделы грузят данные только по нему */
  ctx: ExploreContext | null;
  invalid: boolean;
  /** раздел открыт (до первого открытия ничего не грузим) */
  active: boolean;
}

const isActive = () => typeof location !== 'undefined' && location.hash.replace(/\?.*$/, '') === '#explore';

export const exploreStore = createStore<ExploreState>({
  city: null, date: '', dateTo: '', tab: 'food', near: null, ctx: null, invalid: false, active: isActive(),
});
export const useExplore = () => useStore(exploreStore);

/** Применить город и даты из формы */
export function apply(): boolean {
  const s = exploreStore.get();
  const ctx = makeContext(s.city, s.date, s.dateTo, s.near);
  if (!ctx) { exploreStore.set({ invalid: true }); return false; }
  exploreStore.set({ ctx, invalid: false });
  if (s.city) storage.set(LAST_CITY, s.city);
  return true;
}

export function setCity(city: Picked | null): void {
  exploreStore.set({ city, invalid: false, near: null });
}

/** Выбран город из подсказок или популярных — сразу показываем */
export function pickCity(city: Picked): void {
  exploreStore.set({ city, near: null, invalid: false });
  apply();
}

export function setDates(date: string, dateTo: string): void {
  const s = exploreStore.get();
  exploreStore.set({ date, dateTo: dateTo && dateTo > date ? dateTo : '' });
  // Даты меняют выдачу экскурсий и транспорта — применяем, если город уже выбран
  if (s.ctx) apply();
}

export const setTab = (tab: ExploreTab): void => exploreStore.set({ tab });

/** «Весь город» вместо «рядом с отелем» */
export function clearNear(): void {
  exploreStore.set({ near: null });
  apply();
}

export interface ExploreOpen {
  /** код IATA (если известен) */
  code?: string;
  /** название города */
  name: string;
  tab?: ExploreTab;
  date?: string;
  dateTo?: string;
  near?: ExploreContext['near'];
}

/** Открыть раздел «Места» для города (из окна билета, карточки отеля, ссылки) */
export function openExplore(o: ExploreOpen): void {
  const name = cityLabel(o.name) || o.code || '';
  if (!name) return;
  const now = today();
  const date = o.date && o.date >= now ? o.date : exploreStore.get().date;
  exploreStore.set({
    city: { label: o.code ? `${name} (${o.code})` : name, value: o.code || '' },
    tab: o.tab || exploreStore.get().tab,
    near: o.near || null,
    date: date || '',
    dateTo: o.dateTo && date && o.dateTo > date ? o.dateTo : '',
    invalid: false,
  });
  apply();
  if (typeof location !== 'undefined' && location.hash !== '#explore') location.hash = '#explore';
  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* jsdom */ }
  }
}

/** Раздел открыт впервые: город по умолчанию (если не выбран) */
export function ensureDefaultCity(): void {
  const s = exploreStore.get();
  if (s.city && s.ctx) return;
  const q = currentQuery();
  const city = defaultCity({ query: q, saved: storage.get<Picked | null>(LAST_CITY, null), origin: formStore.get().origin });
  if (city) {
    if (!s.date && q && q.mode === 'exact' && q.departure && q.departure >= today()) {
      exploreStore.set({ date: q.departure, dateTo: q.ret && q.ret > q.departure ? q.ret : '' });
    }
    pickCity(city);
    return;
  }
  // Город пользователя ещё определяется (/api/whereami) — подставим, когда придёт
  const off = formStore.subscribe(() => {
    const origin = formStore.get().origin;
    if (!origin || !origin.value) return;
    off();
    if (!exploreStore.get().ctx) pickCity(origin);
  });
}

/** Ссылка /?ecity=SKD&etab=sights#explore — один раз за загрузку */
let restored = false;
export function restoreFromUrl(): void {
  if (restored || typeof location === 'undefined') return;
  restored = true;
  const link = parseLink(location.search);
  if (!link) return;
  const code = /^[A-Za-z]{3}$/.test(link.city) ? link.city.toUpperCase() : '';
  exploreStore.set({ city: { label: code ? `${code}` : link.city, value: code }, tab: link.tab || 'food', date: link.date });
  apply();
}

/** Раздел открыт или закрыт (по адресу) */
export function syncActive(): void {
  const active = isActive();
  if (active !== exploreStore.get().active) exploreStore.set({ active });
}
